import { useEffect, useRef, type RefObject } from "react";

export type SwipeOptions = {
  /** Finger nach links. */
  onLeft?: () => void;
  /** Finger nach rechts. */
  onRight?: () => void;
  /** Kurzer Wisch nach oben; standardmäßig nur am unteren Scrollrand. */
  onUp?: () => void;
  /** Kurzer Wisch nach unten; standardmäßig nur am oberen Scrollrand. */
  onDown?: () => void;
  /** false: zeitliches Blättern per kurzem Wisch auch innerhalb langer Kalenderzeilen. */
  verticalAtEdges?: boolean;
};

const MIN_X = 60;
const MIN_Y = 90;
const EDGE_X = 24; // Rand links/rechts bleibt der Zurück-Geste des Browsers.
const EDGE_Y = 32; // Rand oben/unten bleibt System-Gesten (Benachrichtigungen, Home).

function scrollableAncestor(el: Element | null): HTMLElement | null {
  for (let node = el as HTMLElement | null; node && node !== document.body; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1) return node;
  }
  return document.scrollingElement as HTMLElement | null;
}

function ignoredTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !(el instanceof Element)) return true;
  if (el.closest("input, textarea, select, [contenteditable=true], [role=dialog], .no-swipe")) return true;
  return document.querySelector("[role=dialog]") !== null;
}

/**
 * Wischgesten per Touch (Touch Events – funktionieren auch in Silk/Fire-Tablets und iOS zuverlässig) und Stift.
 * Senkrechte Gesten greifen standardmäßig nur am Ende des Scrollbereichs. Langsames Scrollen hat Vorrang. Der Inhalt des Elements folgt
 * dem Finger (CSS-Variablen --swipe-dx/--swipe-dy); nach einer Geste wird kein Klick ausgelöst.
 */
export function useSwipe(ref: RefObject<HTMLElement | null> | "window", options: SwipeOptions) {
  const handlers = useRef(options);
  handlers.current = options;

  useEffect(() => {
    const target: HTMLElement | Window | null = ref === "window" ? window : ref.current;
    if (!target) return;
    const el = target instanceof Window ? null : target;
    let start: { x: number; y: number; t: number; atTop: boolean; atBottom: boolean } | null = null;
    let axis: "x" | "y" | null = null;
    let suppressClick = false;

    const reset = () => {
      start = null;
      axis = null;
      el?.style.removeProperty("--swipe-dx");
      el?.style.removeProperty("--swipe-dy");
      el?.classList.remove("swiping");
    };

    const begin = (x: number, y: number, t: EventTarget | null) => {
      if (ignoredTarget(t)) return;
      const h = handlers.current;
      const horizontalOk = (h.onLeft || h.onRight) && x > EDGE_X && x < window.innerWidth - EDGE_X;
      const verticalOk = (h.onUp || h.onDown) && y > EDGE_Y && y < window.innerHeight - EDGE_Y;
      if (!horizontalOk && !verticalOk) return;
      const sc = scrollableAncestor(t as Element);
      const atTop = h.verticalAtEdges === false || !sc || sc.scrollTop <= 1;
      const atBottom = h.verticalAtEdges === false || !sc || sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 2;
      start = { x, y, t: performance.now(), atTop, atBottom };
      axis = null;
    };

    const track = (x: number, y: number) => {
      if (!start) return;
      const dx = x - start.x;
      const dy = y - start.y;
      if (!axis && Math.max(Math.abs(dx), Math.abs(dy)) > 12) {
        axis = Math.abs(dx) > Math.abs(dy) * 1.3 ? "x" : Math.abs(dy) > Math.abs(dx) * 1.3 ? "y" : null;
        const h = handlers.current;
        if ((axis === "x" && !h.onLeft && !h.onRight) || (axis === "y" && !h.onUp && !h.onDown)) {
          reset();
          return;
        }
        if (axis) el?.classList.add("swiping");
      }
      if (axis === "x") el?.style.setProperty("--swipe-dx", `${dx * 0.6}px`);
      if (axis === "y" && ((dy < 0 && start.atBottom) || (dy > 0 && start.atTop))) el?.style.setProperty("--swipe-dy", `${dy * 0.5}px`);
    };

    const finish = (x: number, y: number) => {
      if (!start) return;
      const dx = x - start.x;
      const dy = y - start.y;
      const fast = performance.now() - start.t < 900;
      const s = start;
      const a = axis;
      reset();
      if (!fast) return;
      const h = handlers.current;
      let fired = false;
      if (a === "x" && Math.abs(dx) >= MIN_X) {
        (dx < 0 ? h.onLeft : h.onRight)?.();
        fired = true;
      } else if (a === "y" && Math.abs(dy) >= MIN_Y) {
        if (dy < 0 && s.atBottom && h.onUp) {
          h.onUp();
          fired = true;
        } else if (dy > 0 && s.atTop && h.onDown) {
          h.onDown();
          fired = true;
        }
      }
      if (fired) {
        suppressClick = true;
        window.setTimeout(() => (suppressClick = false), 350);
      }
    };

    const tStart = (e: TouchEvent) => (e.touches.length === 1 ? begin(e.touches[0].clientX, e.touches[0].clientY, e.target) : reset());
    const tMove = (e: TouchEvent) => (e.touches.length === 1 ? track(e.touches[0].clientX, e.touches[0].clientY) : reset());
    const tEnd = (e: TouchEvent) => {
      const t = e.changedTouches[0];
      if (t) finish(t.clientX, t.clientY);
    };
    // Stift ohne Touch-Events (Pointer). Maus bleibt bewusst außen vor.
    const pDown = (e: PointerEvent) => e.pointerType === "pen" && e.isPrimary && begin(e.clientX, e.clientY, e.target);
    const pMove = (e: PointerEvent) => e.pointerType === "pen" && track(e.clientX, e.clientY);
    const pUp = (e: PointerEvent) => e.pointerType === "pen" && finish(e.clientX, e.clientY);
    const click = (e: Event) => {
      if (suppressClick) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const opts: AddEventListenerOptions = { passive: true };
    target.addEventListener("touchstart", tStart as EventListener, opts);
    target.addEventListener("touchmove", tMove as EventListener, opts);
    target.addEventListener("touchend", tEnd as EventListener, opts);
    target.addEventListener("touchcancel", reset, opts);
    target.addEventListener("pointerdown", pDown as EventListener);
    target.addEventListener("pointermove", pMove as EventListener);
    target.addEventListener("pointerup", pUp as EventListener);
    target.addEventListener("click", click, true);
    return () => {
      target.removeEventListener("touchstart", tStart as EventListener);
      target.removeEventListener("touchmove", tMove as EventListener);
      target.removeEventListener("touchend", tEnd as EventListener);
      target.removeEventListener("touchcancel", reset);
      target.removeEventListener("pointerdown", pDown as EventListener);
      target.removeEventListener("pointermove", pMove as EventListener);
      target.removeEventListener("pointerup", pUp as EventListener);
      target.removeEventListener("click", click, true);
    };
  }, [ref]);
}
